<template>
  <div class="flex justify-between items-center w-full h-auto sm:h-12 gap-4 mb-6">
    <div class="flex items-center grow sm:w-auto">
      <div class="mr-6 shrink-0">
        <SectionTitle :title="title" v-if="title" />
      </div>

      <div v-if="searchKey" class="relative w-full max-w-md">
        <div
          class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-500"
        >
          <Icon icon="heroicons-outline:search" class="size-5" />
        </div>
        <input
          type="text"
          :placeholder="$t('admin.search')"
          class="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-main focus:border-transparent transition-shadow"
          @input="searchQuery = ($event.target as HTMLInputElement).value"
        />
        <button
          v-if="searchQuery"
          @click="clearSearch"
          class="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
        >
          <svg class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>
    </div>

    <div class="flex items-center gap-3 shrink-0">
      <div class="relative" v-if="sortingListItems" ref="dropdownRef">
        <button
          @click="isDropdownOpen = !isDropdownOpen"
          class="hidden sm:flex items-center gap-2 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 px-3 py-2 rounded-md text-sm font-medium transition-colors"
        >
          <span>{{ $t('placeholders.sortBy') }}</span>
          <Icon
            icon="mynaui:chevron-down"
            class="w-5 h-5 transition-transform duration-200"
            :class="{ 'rotate-180': isDropdownOpen }"
          />
        </button>
        <Icon
          @click="isDropdownOpen = !isDropdownOpen"
          icon="tabler:filter-2-up"
          class="h-7 w-7 cursor-pointer block sm:hidden text-gray-700"
        />

        <div
          v-if="isDropdownOpen"
          class="absolute right-0 mt-2 w-27 bg-white rounded-md shadow-lg border border-gray-100 z-50 py-1"
        >
          <button
            v-for="(item, index) in sortingListItems"
            :key="index"
            @click="selectSort(item.sortingKey)"
            class="block w-full text-left px-4 py-2 text-sm text-black hover:bg-gray-100 hover:text-main-blue"
            :class="{ 'bg-blue-50 text-main-blue': sortingKey === item.sortingKey }"
          >
            {{ item.text }}
          </button>
        </div>
      </div>

      <slot name="right-buttons"></slot>
    </div>
  </div>
</template>

<script setup lang="ts">
import { i18n } from '@/plugins/i18n'
import type { User } from '@/models/interfaces/auth/User'
import { computed, ref, watch, onMounted, onUnmounted } from 'vue'
import SectionTitle from '@/components/text-elements/SectionTitle.vue'
import { AtlasGroup } from '@/models/enums/geo/AtlasGroup'
import { Icon } from '@iconify/vue'
import type { Project } from '@/models/interfaces/Project'
import type { Resource } from '@/models/interfaces/Resource'

type AdminPages =
  | 'Actors'
  | 'Projects'
  | 'Members'
  | 'Resources'
  | 'Highlights'
  | AtlasGroup.PREDEFINED_MAP
  | AtlasGroup.THEMATIC_DATA
  | 'QgisMaps'
  | 'Comments'

const props = defineProps<{
  page: AdminPages
  items: Project[] | User[] | Resource[]
  sortingListItems?: { sortingKey: string; text: string }[]
  searchKey?: string
}>()

const emit = defineEmits(['updateSortingKey', 'updateSearchQuery'])
const sortingKey = ref('isValidated')
const searchQuery = ref('')
const isDropdownOpen = ref(false)
const dropdownRef = ref<HTMLElement | null>(null)

const closeDropdown = (e: MouseEvent) => {
  if (dropdownRef.value && !dropdownRef.value.contains(e.target as Node)) {
    isDropdownOpen.value = false
  }
}

onMounted(() => document.addEventListener('click', closeDropdown))
onUnmounted(() => document.removeEventListener('click', closeDropdown))

const selectSort = (key: string) => {
  sortingKey.value = key
  isDropdownOpen.value = false
}

const clearSearch = () => {
  searchQuery.value = ''
}

watch(
  () => sortingKey.value,
  () => emit('updateSortingKey', sortingKey.value),
)
watch(
  () => searchQuery.value,
  () => emit('updateSearchQuery', searchQuery.value),
)

const title = computed(() => {
  switch (props.page) {
    case 'Projects':
      return `${props.items.length} ${i18n.t('projects.projects', props.items.length)}`
    case 'Members':
      return `${props.items.length} ${i18n.t('admin.member', props.items.length)}`
    case 'Resources':
      return `${props.items.length} ${i18n.t('resources.resources', props.items.length)}`
    default:
      return ''
  }
})
</script>
